import type { Me } from '@/entities'

/** Tipo de usuario en el portal según su rol. */
export type UserKind = 'ORG_MEMBER' | 'UNKNOWN'

/** Roles que indican que el usuario ya tiene una organización activa. */
const ORG_ROLES = ['ORG_OWNER', 'ORG_MANAGER', 'ORG_ACCOUNTANT'] as const

/**
 * Convención del backend TecoPOS:
 * - `ORG_MEMBER`: usuario con rol de organización (ORG_OWNER, ORG_MANAGER o
 *   ORG_ACCOUNTANT). Tiene organización activa.
 * - `UNKNOWN`: cualquier otro caso — p. ej. un usuario recién registrado que
 *   aún no ha creado su organización. El onboarding es abierto a cualquier
 *   autenticado (se retiró el rol LEAD); "sin organización" ya no es un rol,
 *   sino la ausencia de organización (ver `user.organization` en el dashboard).
 *
 * Se determina inspeccionando `me.roles[].name`.
 */
export function getUserKind(user: Me | null | undefined): UserKind {
  if (!user) return 'UNKNOWN'
  const names = user.roles.map((r) => r.name)
  if (ORG_ROLES.some((r) => names.includes(r))) return 'ORG_MEMBER'
  return 'UNKNOWN'
}

export const isOrgMember = (user: Me | null | undefined): boolean =>
  getUserKind(user) === 'ORG_MEMBER'

/**
 * Etiqueta legible del tipo de usuario para la UI (avatar card, badges).
 * Si el usuario tiene un rol no estándar, prioriza el `displayName` del
 * primer rol con context `PORTAL`.
 */
export function getUserKindLabel(user: Me | null | undefined): string {
  if (!user) return ''
  if (getUserKind(user) === 'ORG_MEMBER') return 'Cliente'
  const portalRole = user.roles.find((r) => r.context === 'PORTAL')
  return portalRole?.displayName ?? 'Usuario'
}
